/**
 * tourSteps.js — Complete TerraMind tutorial step definitions.
 *
 * Architecture
 * ───────────
 * Each SECTION maps to one feature area (page, panel, or workflow).
 * Each STEP within a section targets one element via a `data-tour` attribute.
 *
 * Step schema:
 *   id:        unique string — matches the `data-tour="<id>"` attribute on the DOM element
 *   target:    string | null  — null → centered big card (no spotlight)
 *   page:      string  — route to navigate to before showing this step
 *   titleKey:  string  — i18n key in translations.js (en + el)
 *   descKey:   string  — i18n description key
 *   position:  'right'|'left'|'bottom'|'top'  — preferred tooltip placement
 *   action?:   string  — optional DOM action ('click-demo-field', etc.)
 *
 * HOW TO ADD A NEW STEP
 * ─────────────────────
 * 1. Add `data-tour="<id>"` to the JSX element you want highlighted.
 * 2. Add a step object below with that id, in the appropriate section.
 * 3. Add titleKey + descKey to config/translations.js (both en + el).
 *
 * HOW TO ADD A NEW SECTION
 * ────────────────────────
 * 1. Push a new section object to TOUR_SECTIONS below.
 * 2. Import the icon from @mui/icons-material.
 * 3. Add the titleKey to translations.js (en + el).
 *
 * Section schema:
 *   id:              unique string
 *   titleKey:        i18n key for the section card title in the selector
 *   titleFallback:   Greek fallback text (shown if key is not found)
 *   icon:            MUI SvgIcon component (used on the section card)
 *   color:           hex accent colour for the spotlight border + section card
 *   steps:           Step[]
 */
import { STATUS, TEXT } from '../../theme/colors';
import {
  Map as MapIcon,
  Cloud,
  CalendarMonth,
  NotificationsActive,
  MenuBook,
  Settings as SettingsIcon,
  WaterDrop,
  Agriculture,
  Router as RouterIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';

export const TOUR_SECTIONS = [
  // ─────────────────────────────────────────────────────────────
  // SECTION 1 — Map & Sidebar
  // Covers: field list, live KPI chips, DSS stress banner,
  //         layer switcher, and the map itself.
  // ─────────────────────────────────────────────────────────────
  {
    id: 'map',
    titleKey: 'tourSectionMap',
    titleFallback: 'Χάρτης',
    icon: MapIcon,
    color: '#059669',
    steps: [
      {
        id: 'field-list',
        target: 'field-list',
        page: '/',
        titleKey: 'tourMapFieldsTitle',
        descKey: 'tourMapFieldsDesc',
        showcaseTitleKey: 'tourShowFieldsTitle',
        showcaseDescKey: 'tourShowFieldsDesc',
        position: 'right',
        action: 'click-demo-field',
      },
      {
        id: 'field-kebab',
        target: 'field-kebab',
        page: '/',
        titleKey: 'tourFieldKebabTitle',
        descKey: 'tourFieldKebabDesc',
        position: 'right',
      },
      {
        id: 'map-layer-pill',
        target: 'map-layer-pill',
        page: '/',
        titleKey: 'tourMapLayerPillTitle',
        descKey: 'tourMapLayerPillDesc',
        position: 'bottom',
      },
      {
        id: 'map-container',
        target: 'map-container',
        page: '/',
        titleKey: 'tourMapContainerTitle',
        descKey: 'tourMapContainerDesc',
        showcaseTitleKey: 'tourShowMapTitle',
        showcaseDescKey: 'tourShowMapDesc',
        position: 'right',
      },
      {
        id: 'run-dss-field-btn',
        target: 'run-dss-field-btn',
        page: '/',
        titleKey: 'tourRunDssBtnTitle',
        descKey: 'tourRunDssBtnDesc',
        position: 'right',
        action: 'click-demo-field',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────
  // SECTION 2 — Field Detail Panel
  // Covers: zone health, DSS recommendation cards with the Approve button,
  //         NDVI, and History filters.
  // ─────────────────────────────────────────────────────────────
  {
    id: 'field-detail',
    titleKey: 'tourSectionFieldDetail',
    titleFallback: 'Λεπτομέρειες Χωραφιού',
    icon: WaterDrop,
    color: '#1d4ed8',
    steps: [
      {
        id: 'zone-health',
        target: 'zone-health',
        page: '/',
        titleKey: 'tourMapZonesTitle',
        descKey: 'tourMapZonesDesc',
        showcaseTitleKey: 'tourShowZonesTitle',
        showcaseDescKey: 'tourShowZonesDesc',
        position: 'left',
        action: 'click-demo-field',
      },
      {
        // The field-data plot: moisture at three depths over time, with the
        // wilting-point and field-capacity lines. The client asked to see the
        // data behind the decision in the demo, so the diagnosis beat is
        // followed by the trend the call is read off. Highlight only, no click
        // — the zone popup is already open from the beat before.
        id: 'zone-timeseries',
        target: 'zone-timeseries',
        page: '/',
        titleKey: 'tourTrendTitle',
        descKey: 'tourTrendDesc',
        showcaseTitleKey: 'tourShowTrendTitle',
        showcaseDescKey: 'tourShowTrendDesc',
        position: 'left',
      },
      {
        // Show the recommendation and its reason — no click. The demo reads the
        // call before it acts on it, so the decision gets its own beat and the
        // approval gets the next one. Points at the same recommendation card as
        // the approve beat.
        id: 'dss-decision',
        target: 'dss-recs',
        page: '/',
        titleKey: 'tourDssRecsTitle',
        descKey: 'tourDssRecsDesc',
        showcaseTitleKey: 'tourShowDssTitle',
        showcaseDescKey: 'tourShowDssDesc',
        position: 'left',
      },
      {
        id: 'dss-recs',
        target: 'dss-recs',
        page: '/',
        titleKey: 'tourDssRecsTitle',
        descKey: 'tourDssRecsDesc',
        showcaseTitleKey: 'tourShowApproveTitle',
        showcaseDescKey: 'tourShowApproveDesc',
        position: 'left',
      },
      {
        id: 'ndvi-controls',
        target: 'ndvi-controls',
        page: '/',
        titleKey: 'tourMapNdviTitle',
        descKey: 'tourMapNdviDesc',
        showcaseTitleKey: 'tourShowNdviTitle',
        showcaseDescKey: 'tourShowNdviDesc',
        position: 'left',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────
  // SECTION 3 — Irrigation Schedule
  // Covers: AI suggestion button, paint-to-select calendar,
  //         and the form panel that appears after painting.
  // ─────────────────────────────────────────────────────────────
  {
    id: 'schedule',
    titleKey: 'tourSectionSchedule',
    titleFallback: 'Πρόγραμμα Άρδευσης',
    icon: CalendarMonth,
    color: '#7c3aed',
    steps: [
      {
        id: 'schedule-field-filter',
        target: 'schedule-field-filter',
        page: '/schedules',
        titleKey: 'tourScheduleFieldFilterTitle',
        descKey: 'tourScheduleFieldFilterDesc',
        position: 'bottom',
      },
      {
        id: 'dss-button',
        target: 'dss-button',
        page: '/schedules',
        titleKey: 'tourDssButtonTitle',
        descKey: 'tourDssButtonDesc',
        position: 'bottom',
      },
      {
        id: 'weekly-calendar',
        target: 'weekly-calendar',
        page: '/schedules',
        titleKey: 'tourWeeklyCalendarTitle',
        descKey: 'tourWeeklyCalendarDesc',
        showcaseTitleKey: 'tourShowScheduleTitle',
        showcaseDescKey: 'tourShowScheduleDesc',
        position: 'bottom',
      },
      {
        id: 'schedule-paint-toggle',
        target: 'schedule-paint-toggle',
        page: '/schedules',
        titleKey: 'tourSchedulePaintTitle',
        descKey: 'tourSchedulePaintDesc',
        position: 'bottom',
      },
      {
        id: 'schedule-form-panel',
        target: 'schedule-form-panel',
        page: '/schedules',
        titleKey: 'tourScheduleFormPanelTitle',
        descKey: 'tourScheduleFormPanelDesc',
        showcaseTitleKey: 'tourShowProgrammeTitle',
        showcaseDescKey: 'tourShowProgrammeDesc',
        position: 'left',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────
  // SECTION 4 — Weather & Spray Conditions
  // ─────────────────────────────────────────────────────────────
  {
    id: 'weather',
    titleKey: 'tourSectionWeather',
    titleFallback: 'Καιρός',
    icon: Cloud,
    color: '#2563eb',
    steps: [
      {
        id: 'weather-hero',
        target: 'weather-hero',
        page: '/weather',
        titleKey: 'tourWeatherHeroTitle',
        descKey: 'tourWeatherHeroDesc',
        showcaseTitleKey: 'tourShowWeatherTitle',
        showcaseDescKey: 'tourShowWeatherDesc',
        position: 'bottom',
      },
      {
        id: 'spray-window',
        target: 'spray-window',
        page: '/weather',
        titleKey: 'tourSprayWindowTitle',
        descKey: 'tourSprayWindowDesc',
        position: 'bottom',
      },
      {
        id: 'weather-history-charts',
        target: 'weather-history-charts',
        page: '/weather',
        titleKey: 'tourWeatherHistoryChartsTitle',
        descKey: 'tourWeatherHistoryChartsDesc',
        position: 'bottom',
      },
    ],
  },

  // SECTION 5, Analytics, was removed with the Δεδομένα Αγρού page (audit
  // step 4 M8, George 16/10/2026): the charts are now a section of each
  // field's page, which the field steps above already open.

  // ─────────────────────────────────────────────────────────────
  // SECTION 7 — Alerts & Notifications
  // Covers: notification bell in the header, alerts list page.
  // ─────────────────────────────────────────────────────────────
  {
    id: 'alerts',
    titleKey: 'tourSectionAlerts',
    titleFallback: 'Ειδοποιήσεις',
    icon: NotificationsActive,
    color: STATUS.error,
    steps: [
      {
        id: 'notification-bell',
        target: 'notification-bell',
        page: '/',
        titleKey: 'tourNotificationBellTitle',
        descKey: 'tourNotificationBellDesc',
        position: 'bottom',
      },
      {
        id: 'alerts-page',
        target: 'alerts-page',
        page: '/alerts',
        titleKey: 'tourAlertsPageTitle',
        descKey: 'tourAlertsPageDesc',
        showcaseTitleKey: 'tourShowAlertsTitle',
        showcaseDescKey: 'tourShowAlertsDesc',
        position: 'bottom',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────
  // SECTION 8 — Field Diary & History
  // ─────────────────────────────────────────────────────────────
  {
    id: 'diary',
    titleKey: 'tourSectionDiary',
    titleFallback: 'Ημερολόγιο & Ιστορικό',
    icon: MenuBook,
    color: '#ca8a04',
    steps: [
      {
        id: 'diary-page',
        target: 'diary-page',
        page: '/journal',
        titleKey: 'tourDiaryPageTitle',
        descKey: 'tourDiaryPageDesc',
        position: 'bottom',
      },
      {
        id: 'diary-add-btn',
        target: 'diary-add-btn',
        page: '/journal',
        titleKey: 'tourDiaryAddBtnTitle',
        descKey: 'tourDiaryAddBtnDesc',
        position: 'bottom',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────
  // SECTION 9 — Field & Zone Management
  // Covers: adding a field via wizard, drawing zone polygons,
  //         editing zone configuration.
  // ─────────────────────────────────────────────────────────────
  {
    id: 'field-management',
    titleKey: 'tourSectionFieldManagement',
    titleFallback: 'Διαχείριση Χωραφιών',
    icon: Agriculture,
    color: '#16a34a',
    steps: [
      {
        id: 'add-field-btn',
        target: 'add-field-btn',
        page: '/',
        titleKey: 'tourAddFieldBtnTitle',
        descKey: 'tourAddFieldBtnDesc',
        position: 'right',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────
  // SECTION 10 — Hardware
  // Covers: what each field can actually do, the device fleet, and the
  //         valve controllers. This is the section the client asks about
  //         most and the one the tour could not previously reach.
  // ─────────────────────────────────────────────────────────────
  {
    id: 'hardware',
    titleKey: 'tourSectionHardware',
    titleFallback: 'Εξοπλισμός',
    icon: RouterIcon,
    color: '#0e7490',
    steps: [
      {
        id: 'hardware-coverage',
        target: 'hardware-coverage',
        page: '/devices',
        titleKey: 'tourCoverageTitle',
        descKey: 'tourCoverageDesc',
        showcaseTitleKey: 'tourShowHardwareTitle',
        showcaseDescKey: 'tourShowHardwareDesc',
        position: 'top',
      },
      {
        id: 'hardware-tabs',
        target: 'hardware-tabs',
        page: '/devices',
        titleKey: 'tourHardwareTabsTitle',
        descKey: 'tourHardwareTabsDesc',
        showcaseTitleKey: 'tourShowFleetTitle',
        showcaseDescKey: 'tourShowFleetDesc',
        position: 'bottom',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────
  // SECTION 11 — Reports
  // Covers: the reports the platform generates on its own, kept in the
  //         app rather than only in an inbox.
  // ─────────────────────────────────────────────────────────────
  {
    id: 'reports',
    titleKey: 'tourSectionReports',
    titleFallback: 'Αναφορές',
    icon: DescriptionIcon,
    color: '#7c3aed',
    steps: [
      {
        // The on-demand generate button — a farmer makes a fresh report the
        // moment he wants one, not only when the morning email lands.
        id: 'reports-generate',
        target: 'reports-generate',
        page: '/reports',
        titleKey: 'tourReportsGenerateTitle',
        descKey: 'tourReportsGenerateDesc',
        showcaseTitleKey: 'tourShowReportGenerateTitle',
        showcaseDescKey: 'tourShowReportGenerateDesc',
        position: 'bottom',
      },
      {
        id: 'reports-list',
        target: 'reports-list',
        page: '/reports',
        titleKey: 'tourReportsListTitle',
        descKey: 'tourReportsListDesc',
        showcaseTitleKey: 'tourShowReportsTitle',
        showcaseDescKey: 'tourShowReportsDesc',
        position: 'right',
      },
      {
        id: 'reports-viewer',
        target: 'reports-viewer',
        page: '/reports',
        titleKey: 'tourReportsViewerTitle',
        descKey: 'tourReportsViewerDesc',
        showcaseTitleKey: 'tourShowReportTitle',
        showcaseDescKey: 'tourShowReportDesc',
        position: 'left',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────
  // SECTION 10 — Settings & Preferences
  // Covers: language toggle in the header, auto-accept DSS toggle,
  //         water-savings priority slider.
  // ─────────────────────────────────────────────────────────────
  {
    id: 'settings',
    titleKey: 'tourSectionSettings',
    titleFallback: 'Ρυθμίσεις',
    icon: SettingsIcon,
    color: TEXT.secondary,
    steps: [
      {
        id: 'language-toggle',
        target: 'language-toggle',
        page: '/',
        titleKey: 'tourLanguageToggleTitle',
        descKey: 'tourLanguageToggleDesc',
        position: 'bottom',
      },
      {
        id: 'season-chip',
        target: 'season-chip',
        page: '/journal',
        titleKey: 'tourSeasonChipTitle',
        descKey: 'tourSeasonChipDesc',
        position: 'bottom',
      },
    ],
  },
];

/* ─────────────────────────────────────────────────────────────
   Utility helpers — used by TourContext and TourOverlay.
   Do not modify these unless the context/overlay API changes.
   ──────────────────────────────────────────────────────────── */

/** Total step count across all sections. */
export const getTotalSteps = () =>
  TOUR_SECTIONS.reduce((sum, section) => sum + section.steps.length, 0);

/**
 * Flattens all sections into a single ordered array of steps,
 * each annotated with section metadata needed by TourOverlay.
 */
export const flattenSteps = () => {
  const flat = [];
  TOUR_SECTIONS.forEach((section, sectionIndex) => {
    section.steps.forEach((step, stepInSection) => {
      flat.push({
        ...step,
        sectionId: section.id,
        sectionIndex,
        sectionTitleKey: section.titleKey,
        sectionTitleFallback: section.titleFallback,
        sectionColor: section.color,
        sectionIcon: section.icon,
        stepInSection,
        sectionStepCount: section.steps.length,
      });
    });
  });
  return flat;
};

/* ── Compatibility aliases used by TourOverlay ─────────────────────── */

/** Flat step array indexed by global step number. */
export const TOUR_STEPS = flattenSteps();

/** Act (section) metadata — color + titleKey per section. */
export const ACTS = TOUR_SECTIONS.map((s) => ({
  color: s.color,
  titleKey: s.titleKey,
  icon: s.icon,
}));

/** Given a global step index, return the section (act) index it belongs to. */
export const getActIndex = (stepIndex) => {
  const step = TOUR_STEPS[stepIndex];
  return step ? step.sectionIndex : 0;
};

/**
 * SHOWCASE_FLOW — the demo, as opposed to the tutorial.
 *
 * The sections above are a manual: thorough, in feature order, and dull to
 * watch. Playing all thirty of them at a stand is the thing the client called
 * boring, and he was right. This is the other use of the same steps — a
 * curated run through the real app that tells one story:
 *
 *   here is your farm  ->  here is what the soil is doing  ->  here is what
 *   we advise and why  ->  here is the satellite and the weather behind it ->
 *   here is it watering on its own  ->  here is the hardware it runs on  ->
 *   here is the report that lands in your inbox every morning.
 *
 * Every id below is a real step on a real screen with real data. Nothing here
 * is written for the demo: cutting this list changes what the demo shows, not
 * what the product is.
 *
 * Order matters more than completeness. Anything a buyer would not ask about
 * in the first five minutes — the diary, the alert bell, the language toggle,
 * adding a field — stays in the tutorial where it belongs.
 */
export const SHOWCASE_FLOW = [
  // A morning in the life of the grower, in the order he lives it: open the
  // app and see where every field stands, read the soil on the one that needs
  // him, get the decision and the reason, watch it become a schedule he can
  // adjust, then the weather and the hardware behind it.
  //
  // Mechanics: 'field-list' opens a field, and the next three beats live
  // INSIDE the panel that opens. They must stay contiguous and nothing on
  // another route may sit among them, or the panel closes mid-sentence. See
  // the contiguity test. The decision beat is last inside the panel so it
  // lands right before the schedule it produces.
  'field-list',          // open the app: the farm, and what needs you
  'zone-health',         // ── panel ── the water where the roots are
  'zone-timeseries',     // ── panel ── the field-data plot the call is read off
  'dss-decision',        // ── panel ── the decision: irrigate, how long, why (read it)
  'dss-recs',            // ── panel ── approve it (the click)
  'weekly-calendar',     // and it is already on the calendar
  // No «week ahead, agreed» beat since 16/10/2026: the runs it approved were
  // projected by the seed, not computed by the engine.
  'alerts-page',         // what needs you, flagged and sorted
  'weather-hero',        // the weather behind the call
  'hardware-coverage',   // on the gear you already own
  'reports-generate',    // a full report the moment you want one
  'reports-viewer',      // the document it produces, ready to hand over
];

// The fleet tab ('hardware-tabs') is deliberately NOT in the flow. It lists the
// IoT-gateway provisioning registry (sensors.IoTDevice), which the showcase
// clone does not populate — the demo orchard's blocks carry GaiaSense-style
// sensor devices, not IoT4H2O gateways — so the tab reads "No devices
// registered yet" under a caption promising a device list. The coverage beat
// above already tells the hardware story with the real sensors, per field, and
// is the honest version. An empty tab at a trade stand sells nothing.

/**
 * Panel beats: they only exist while the zone panel is open, so they must
 * stay contiguous and immediately follow the beat that opens it.
 */
export const SHOWCASE_PANEL_BEATS = ['zone-health', 'zone-timeseries', 'dss-decision', 'dss-recs'];

/**
 * Full-screen argument cards, keyed by the flow position they precede.
 * The deck's spine: the problem, then diagnose, decide, act.
 */
export const SHOWCASE_CHAPTERS = {
  0: 'Problem',    // before field-list: watering by feel is a gamble
  1: 'Diagnosis',  // before zone-health + zone-timeseries: it reads the whole field
  3: 'Decision',   // before dss-decision: it makes the call (read the reason)
  // position 4 (dss-recs, the approve click) is a continuation of Decision — no card
  5: 'Action',     // before weekly-calendar: it carries the call out
};

/**
 * Which controls the viewer may touch during the presentation.
 *
 * A stand demo that lets someone wander into an unrelated page loses the
 * thread and cannot be recovered by whoever is presenting. Navigation is
 * suppressed for the length of a run; the player's own controls stay live.
 */
export const SHOWCASE_LOCKED = true;



/**
 * Resolve the flow to {sectionId, stepInSection} pairs the tour can play.
 *
 * Ids that no longer exist are dropped rather than throwing: a missing step
 * should shorten the demo, not blank the screen at a trade stand.
 * showcaseFlow.test.js fails on any id that does not resolve, so this
 * forgiving path is a safety net and never the normal case.
 */
export function resolveShowcaseFlow() {
  const out = [];
  SHOWCASE_FLOW.forEach((stepId) => {
    TOUR_SECTIONS.forEach((section) => {
      const idx = section.steps.findIndex((st) => st.id === stepId);
      if (idx >= 0) out.push({ sectionId: section.id, stepInSection: idx, stepId });
    });
  });
  return out;
}
